import { Dialog } from "@mui/material";
import Box from "@mui/system/Box";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { languages, locations } from "src/slices/brief/helpers";
import { getProjects, projects } from "src/slices/projects";
// import { useDispatch } from "src/store";
import { nanoid } from "@reduxjs/toolkit";
import { useRouter } from "next/router";
import { getUserDetails } from "src/slices/articles/thunks";
import { optimizeArticle } from "src/slices/optimizeContent/thunks";
import { getCurrentUser } from "src/slices/system/thunks";
import { useDispatch } from "src/store";
import * as yup from "yup";
import ChooseKeyword from "./chooseKeyword";
import Error from "./error";
import Loader from "./loader";
import Success from "./success";

interface Props {
  open: boolean;
  limit: boolean;
  isRedirect?: boolean;
  seoMissionsKeyword?: string;
  seoMissionsKeywordId?: string;
  isSeoMissionsBrief?: boolean;
  isShowInputs?: boolean;
  link?: string;
  inputValues?: {
    keyword: string;
    location: (typeof locations)[0];
    language: string;
    stats: boolean;
    insights: string;
    articleLink: string;
    project: string;
  };
  onClose?: () => void;
  onCreated: (id: string) => void;
}

export const OptimizeContentDialog = ({
  open,
  limit,
  isRedirect,
  seoMissionsKeyword,
  seoMissionsKeywordId,
  isShowInputs,
  link,
  inputValues,
  onClose,
  onCreated,
}: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const [currentStep, setCurrentStep] = useState(0);
  const id = nanoid();

  let location = {
    location_name: "United States",
    country_iso_code: "US",
  };

  try {
    location = JSON.parse(localStorage.getItem("brief-location"));
  } catch (error) {}

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getCurrentUser());
  }, []);
  const Projects = useSelector(projects);
  const language = localStorage.getItem("brief-language") ?? "English";
  let project = null;
  try {
    project = JSON.parse(localStorage.getItem("brief-project")) ?? Projects[0];
  } catch (error) {}

  const formik = useFormik<{
    keyword: string;
    location: (typeof locations)[0];
    language: string;
    project: {
      projectName: string;
      projectId: string;
      projectDomain: string;
      isGSCIntegrated: boolean;
      companyDetails: any;
      GSCsiteUrl: string;
      excludedLinks: string[];
    };
    articleLink: string;
    insights: string;
    stats: boolean;
    interlinking: boolean;
    aiEvalRefineAgent: boolean;
  }>({
    initialValues: {
      keyword: "",
      location,
      language,
      project,
      articleLink: link || "",
      insights: "",
      stats: false,
      interlinking: false,
      aiEvalRefineAgent: true,
    },

    onSubmit: async (values) => {
      if (limit) {
        setCurrentStep(3);
        return;
      }
      setCurrentStep(1);
      const languageCode = languages.find(
        (value) => value.languageName === formik.values.language
      ).languageCode;
      // projects commented out
      const seoMissionBriefId = seoMissionsKeywordId ?? "";

      const projectId = formik.values.project.projectId;
      const projectName = formik.values.project.projectName;
      const projectDomain = formik.values.project.projectDomain;
      const GSCsiteUrl = formik.values.project.GSCsiteUrl;
      const excludedLinks = formik.values.project.excludedLinks;
      const userDetails = (await dispatch(getUserDetails())).payload?.user;
      let userId = "";
      if (userDetails?.teamId && userDetails?.teamId !== "undefined") {
        userId = userDetails?.ownerUsername;
      } else {
        userId = userDetails?.Username;
      }
      // let competitors = await dispatch(
      //   getCompetitors({
      //     keyword: formik.values.keyword,
      //     locationName: formik.values.location.location_name,
      //     languageCode,
      //     id: id,
      //   })
      // );

      // const competitorsData = competitors?.payload?.data?.result[0]?.items;

      // let organicCompetitors = [];
      // if (competitorsData) {
      //   organicCompetitors =
      //     competitorsData
      //       .filter((item) => item.type === "organic" && item.url)
      //       .map((item) => item.url) || [];
      // }

      let optimizedContent = await dispatch(
        optimizeArticle({
          id: id,
          userId: userId,
          keyword: formik.values.keyword,
          articleLink: formik.values.articleLink,
          location: formik.values.location,
          languageCode,
          languageName: formik.values.language,
          projectId: projectId,
          projectName: projectName,
          seoMissionsBriefId: seoMissionBriefId,
          insights: formik.values.insights,
          stats: formik.values.stats,
          interlinking: formik.values.interlinking,
          competitors: [],
          GSCsiteUrl: GSCsiteUrl,
          excludedLinks: excludedLinks,
          companyDetails: formik.values.project.companyDetails,
          projectDomain: formik.values.project.projectDomain,
          aiEvalRefineAgent: formik.values.aiEvalRefineAgent,
        })
      );

      if (optimizedContent.payload.status_code === 200) {
        formik.setFieldValue("keyword", "");
        setCurrentStep(2);
        onCreated(id);
      }
    },
    validationSchema: yup.object({
      keyword: yup.string().max(255).required("Focus keyword is required"),
      articleLink: yup.string().max(255).required("Article link is required"),
      location: yup.object().required("Location is required").nullable(),
      project: yup.object().required("Project is required").nullable(),
    }),
  });

  const handleOnClose = () => {
    formik.setFieldValue("keyword", "");
    formik.setFieldValue("articleLink", "");
    formik.setFieldValue("insights", "");
    onClose?.();
    setCurrentStep(0);
  };

  const handleCreateAnotherBrief = () => {
    formik.setFieldValue("keyword", "");
    formik.setFieldValue("articleLink", "");
    formik.setFieldValue("insights", "");
    setCurrentStep(0);
    onCreated(id);
  };

  useEffect(() => {
    formik.setFieldValue("keyword", seoMissionsKeyword);
  }, [seoMissionsKeyword]);

  useEffect(() => {
    if (isShowInputs && inputValues) {
      formik.setFieldValue("keyword", inputValues?.keyword);
      formik.setFieldValue("location", inputValues?.location);
      formik.setFieldValue("language", inputValues?.language);
      formik.setFieldValue("project", inputValues?.project);
      formik.setFieldValue("articleLink", inputValues?.articleLink);
      formik.setFieldValue("insights", inputValues?.insights);
      formik.setFieldValue("stats", inputValues?.stats);
    }
  }, [inputValues, isShowInputs]);

  useEffect(() => {
    if (link) {
      formik.setFieldValue("articleLink", link);
    }
  }, [link]);

  const componentsMap = {
    0: <ChooseKeyword formik={formik} isShowInput={isShowInputs} />,
    1: <Loader />,
    //add a loading component here for step 2
    2: (
      <Success
        isRedirect={isRedirect}
        onClose={handleOnClose}
        onCreated={handleCreateAnotherBrief}
      />
    ),
    3: <Error onClose={handleOnClose} />,
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleOnClose}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, mt: 5.5 }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "8px",
          borderRadius: "8px 8px 0px 0px",
          background:
            "linear-gradient(60.66deg, #FFD875 -3.87%, #DA00C8 26.08%, #6A00FF 56.49%)",
        }}
      />

      <form onSubmit={formik.handleSubmit}>{componentsMap[currentStep]}</form>
    </Dialog>
  );
};
